import { h } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";
import { Trans } from "@lingui/react/macro";
import { CircleCheck, CircleXmark } from "../../../../utils/icons";
import { ManualWithdrawFormData, ManualWithdrawMaxAmount, ManualWithdrawPageComponentProps } from "./types";
import { ManualWithdrawService } from "./service";
import Config from "../../../../config";
import formatCurrency from "../../../../../../services/currency";

const MIN_WITHDRAW = 500;

function PhoneIcon() {
    return (
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
    );
}

export function ManualWithdrawPage(props: ManualWithdrawPageComponentProps) {
    const service = useRef(new ManualWithdrawService(Config.softionAPI));
    const [maxAmount, setMaxAmount] = useState<ManualWithdrawMaxAmount | null>(null);
    const [loadingMaxAmount, setLoadingMaxAmount] = useState(true);
    const [telebirrAddress, setTelebirrAddress] = useState<string>('');
    const [amount, setAmount] = useState<string>('');
    const [step, setStep] = useState<'form' | 'submitting' | 'success' | 'error'>('form');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        service.current.getMaxAmount().then(data => {
            setMaxAmount(data);
            setLoadingMaxAmount(false);
        }).catch(() => {
            setMaxAmount(null);
            setLoadingMaxAmount(false);
        });
    }, []);

    const handleWithdraw = async () => {
        const val = parseFloat(amount);
        if (isNaN(val) || val < MIN_WITHDRAW) return;
        if (!telebirrAddress.trim()) return;
        setStep('submitting');
        try {
            await service.current.requestWithdrawal({ telebirr_address: telebirrAddress.trim(), amount });
            setStep('success');
        } catch (err: any) {
            setError(err?.message || 'An unexpected error occurred.');
            setStep('error');
        }
    };

    const reset = () => {
        setStep('form');
        setTelebirrAddress('');
        setAmount('');
        setError(null);
    };

    if (loadingMaxAmount) {
        return (
            <div class="grow w-full flex flex-col items-center justify-center bg-slate-900 overflow-auto">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
            </div>
        );
    }

    if (step === 'submitting') {
        return (
            <div class="grow w-full flex flex-col items-center justify-center bg-slate-900 overflow-auto">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div class="grow w-full flex flex-col items-center justify-center bg-slate-900 overflow-auto">
                <div class="flex flex-col items-center gap-4 p-6 text-center max-w-sm">
                    <CircleCheck class="h-32 text-green-500" />
                    <div class="text-xl font-bold text-white"><Trans>Withdrawal Submitted</Trans></div>
                    <div class="text-base text-slate-300">
                        <Trans>Your withdrawal request has been submitted. Our team will process it and send the money to your Telebirr account shortly.</Trans>
                    </div>
                    <button class="px-5 py-3 rounded-lg bg-yellow-500 text-slate-900 font-bold text-xl" onClick={reset}>
                        <Trans>New Withdrawal</Trans>
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'error') {
        return (
            <div class="grow w-full flex flex-col items-center justify-center bg-slate-900 overflow-auto">
                <div class="flex flex-col items-center gap-4 p-6 text-center max-w-sm">
                    <CircleXmark class="h-32 text-red-600" />
                    <div class="text-xl font-bold text-white"><Trans>Error</Trans></div>
                    <div class="text-base text-red-600">{error}</div>
                    <button class="px-5 py-3 rounded-lg bg-yellow-500 text-slate-900 font-bold text-xl" onClick={() => setStep('form')}>
                        <Trans>Try Again</Trans>
                    </button>
                </div>
            </div>
        );
    }

    const canSubmit = telebirrAddress.trim() !== '' && parseFloat(amount) >= MIN_WITHDRAW;

    return (
        <div class="grow w-full flex flex-col items-center bg-slate-900 overflow-auto">
            <div class="w-full max-w-sm p-4 flex flex-col gap-5">
                <h2 class="text-2xl font-bold text-white"><Trans>Manual Withdrawal</Trans></h2>

                <div class="flex items-center gap-3">
                    <div class="h-px flex-1 bg-slate-700" />
                    <span class="text-yellow-500 text-xs font-semibold tracking-widest uppercase"><Trans>Telebirr</Trans></span>
                    <div class="h-px flex-1 bg-slate-700" />
                </div>

                <div>
                    <div class="flex items-center gap-1.5 mb-1.5">
                        <PhoneIcon />
                        <span class="text-slate-300 text-xs uppercase tracking-wide"><Trans>Telebirr Number</Trans></span>
                    </div>
                    <input
                        class="w-full border rounded-lg bg-slate-800 border-slate-600 text-white py-2.5 px-3 text-base placeholder-slate-500 outline-none focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/30 transition-all"
                        type="text"
                        value={telebirrAddress}
                        onInput={e => setTelebirrAddress((e.target as HTMLInputElement).value)}
                        placeholder="09XXXXXXXX"
                        maxLength={20}
                    />
                </div>

                <div>
                    <div class="flex justify-between items-center mb-1.5">
                        <span class="text-slate-300 text-xs uppercase tracking-wide"><Trans>Amount</Trans></span>
                        <span class="text-yellow-500 text-xs">
                            <Trans>Min:</Trans> {formatCurrency(MIN_WITHDRAW)} ETB
                        </span>
                        {maxAmount ? (
                            <span class="text-yellow-500 text-xs">
                                <Trans>Max:</Trans> {formatCurrency(maxAmount.max)} {maxAmount.currency}
                            </span>
                        ) : null}
                    </div>
                    <div class="flex gap-2">
                        <input
                            class="flex-1 border rounded-lg bg-slate-800 border-slate-600 text-white py-2.5 px-3 text-base placeholder-slate-500 outline-none focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/30 transition-all"
                            type="number"
                            value={amount}
                            onInput={e => setAmount((e.target as HTMLInputElement).value)}
                            placeholder={window.Translation('Enter Amount')}
                            min={String(MIN_WITHDRAW)}
                            step="1"
                        />
                        <button
                            class="px-6 py-2.5 rounded-lg bg-yellow-500 text-slate-900 font-bold text-base hover:bg-yellow-400 active:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleWithdraw}
                            disabled={!canSubmit}
                        >
                            <Trans>Withdraw</Trans>
                        </button>
                    </div>
                </div>

                <div class="bg-slate-800/80 rounded-xl p-4 border border-slate-700/50 text-slate-300 text-sm space-y-3">
                    <div>
                        <h3 class="text-base font-bold text-white">
                            <Trans>How it works</Trans>
                        </h3>
                    </div>
                    <ol class="list-decimal pl-4 text-slate-300 space-y-1 leading-relaxed">
                        <li><Trans>Enter your Telebirr phone number and the amount you want to withdraw.</Trans></li>
                        <li><Trans>The amount will be deducted from your balance immediately.</Trans></li>
                        <li><Trans>Our team will send the money to your Telebirr account.</Trans></li>
                        <li><Trans>Once sent, your withdrawal will be marked as completed.</Trans></li>
                    </ol>
                    <div class="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3 -mx-1 text-amber-400 text-xs leading-relaxed">
                        <Trans>Withdrawal requests are processed during business hours. If you have any issues, contact support.</Trans>
                    </div>
                </div>

                <div class="flex justify-center">
                    <button class="text-slate-500 hover:text-white text-sm transition-colors px-4 py-1" onClick={reset}>
                        <Trans>Cancel</Trans>
                    </button>
                </div>
            </div>
        </div>
    );
}
